'use client';

import { useActionState, useEffect, useState } from 'react';
import { applyVoucherFormAction, removeVoucherFormAction } from '../../../actions/voucher/voucherFormActions';
import type { CartVoucherProps } from './types';
import { LocalStorage } from 'utils';
import { trackVoucherButtonClick, trackVoucherApply, trackVoucherRemove } from '../../../analytics/overview';
import { ActiveVoucher } from './ActiveVoucher';
import { DefaultVoucher } from './DefaultVoucher';

const STORAGE_KEY = 'PIXUM_CART_VOUCHER';

/**
 * Cart Voucher Component
 *
 * Allows users to apply or remove discount voucher codes
 * Supports pre-fill from URL params or localStorage
 */
export function CartVoucher({ activeVoucher, urlVoucherCode, constants }: CartVoucherProps) {
  const [isOpen, setIsOpen] = useState(!!urlVoucherCode || !!activeVoucher);
  const [inputValue, setInputValue] = useState(urlVoucherCode || '');
  const [hasError, setHasError] = useState(false);

  const [applyState, applyFormAction, isApplying] = useActionState(applyVoucherFormAction, { success: false });
  const [removeState, removeFormAction, isRemoving] = useActionState(removeVoucherFormAction, { success: false });

  // Pre-fill from localStorage (only if no URL param and no active voucher)
  useEffect(() => {
    if (!urlVoucherCode && !activeVoucher) {
      const storedCode = LocalStorage.getItem(STORAGE_KEY);

      if (storedCode) {
        setInputValue(storedCode);
        setIsOpen(true);
      }
    }
  }, [urlVoucherCode, activeVoucher]);

  useEffect(() => {
    if (applyState.success) {
      trackVoucherApply(inputValue, true);
      LocalStorage.removeItem(STORAGE_KEY);
    } else if (applyState.error) {
      setHasError(true);
      trackVoucherApply(inputValue, false);
    }
  }, [applyState, inputValue]);

  useEffect(() => {
    if (removeState.success && removeState.code) {
      trackVoucherRemove(removeState.code);
      setInputValue('');
    }
  }, [removeState]);

  const handleFocus = () => {
    setHasError(false);
  };

  const handleAccordionClick = () => {
    if (!isOpen) {
      trackVoucherButtonClick();
    }
    setIsOpen(!isOpen);
  };

  if (activeVoucher) {
    return (
      <ActiveVoucher
        activeVoucher={activeVoucher}
        isOpen={isOpen}
        isRemoving={isRemoving}
        onAccordionClick={handleAccordionClick}
        removeFormAction={removeFormAction}
        constants={{
          headlineText: constants.headlineText,
          activeText: constants.activeText,
          removeButtonText: constants.removeButtonText
        }}
      />
    );
  }

  return (
    <DefaultVoucher
      inputValue={inputValue}
      isOpen={isOpen}
      isApplying={isApplying}
      hasError={hasError}
      errorMessage={!applyState.success ? applyState.error || '' : ''}
      onAccordionClick={handleAccordionClick}
      onInputChange={e => setInputValue(e.target.value)}
      onFocus={handleFocus}
      applyFormAction={applyFormAction}
      constants={{
        headlineText: constants.headlineText,
        inputLabel: constants.inputLabel,
        submitButtonText: constants.submitButtonText
      }}
    />
  );
}
